import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { UtilService } from '../../../../services/util.service';
import { SbDateTime, SbLocalDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { API, LocalizedFramedReportingDTO, PlayerCullDTO, Weekday } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-airtime-reporting',
  templateUrl: './airtime.component.html',
  styleUrls: ['./airtime.component.scss'],
  standalone: false,
})
export class AirtimeComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  filteredPlayerCulls: PlayerCullDTO[];
  interval: number;
  debounceTimer: ReturnType<typeof setTimeout>;
  timeFrameData;
  loading: boolean;

  _from?: SbDateTime;
  get from(): SbDateTime {
    return this._from;
  }

  set from(val: SbDateTime) {
    this._from = val;
  }

  _till?: SbDateTime;
  get till(): SbDateTime {
    return this._till;
  }

  set till(val: SbDateTime) {
    this._till = val;
  }

  constructor(
    public readonly util: UtilService,
    private readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    private readonly api: API,
    public readonly permissionService: PermissionService
  ) {
    this.filteredPlayerCulls = undefined;
    this.debounceTimer = null;
    this.interval = 86400;
    this.loading = true;
  }

  ngOnInit(): void {
    this.initTimeRange();
    this.requestDebounced();
  }

  initTimeRange() {
    if (!this.from && !this.till) {
      this.from = new SbLocalDateTime(
        DateTime.local().startOf('day').minus({
          months: 3,
          day: 1,
        })
      );
      this.till = new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
    }
  }

  refreshFromServer() {
    this.loading = true;
    this.initTimeRange();
    this.api
      .ss_nm_Reporting_airtimeQuota({
        productive: true,
        interval: this.interval,
        weekday: [Weekday.MONDAY, Weekday.TUESDAY, Weekday.WEDNESDAY, Weekday.THURSDAY, Weekday.FRIDAY, Weekday.SATURDAY],
        hour: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
        from: new SbLocalDateTime(DateTime.fromISO(this.from.toString())),
        till: new SbLocalDateTime(DateTime.fromISO(this.till.toString())),
        playerCull: this.util.arrayToIdArray(this.filteredPlayerCulls),
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (reportingData: LocalizedFramedReportingDTO) => {
          this.timeFrameData = reportingData.buckets[0].frames;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  setPlayerCulls(e: PlayerCullDTO[]) {
    if (!e) {
      this.filteredPlayerCulls = [];
    } else {
      this.filteredPlayerCulls = e;
    }
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  setInterval(interval) {
    this.interval = interval;
    this.requestDebounced();
  }

  setTimeFrame(dates?: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (dates?.fromDate && dates?.toDate) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.requestDebounced();
    }
  }
}
